import React, {memo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, UiBox, UiButton, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {EMERGENCY_CONTRACEPTION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Notice = memo(() => {
  return (
    <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft">
      <UiText fontSize="md" fontWeight="bold" mt={spacing.small}>
        Notice regarding blood pressure
      </UiText>
      <UiText fontSize="sm" mt={spacing.tiny}>
        Emergency contraception may affect blood pressure. Please note that without a blood pressure reading, it may be
        more difficult for us to advise you about your risk levels with respect to emergency contraception.
      </UiText>
      <UiText fontSize="sm" mt={spacing.small}>
        You can have your blood pressure checked for free at most local pharmacies.
      </UiText>
      <UiText fontSize="sm" mt={spacing.small}>
        If you choose to proceed, you agree that we shall not be responsible for any issues associated with blood
        pressure due to the lack of a blood pressure reading.
      </UiText>
    </UiBox>
  );
});

const Questionnaire9Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH]?.data,
  );

  const {inputs, setInputs} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    selectedBlood: currentAnswer?.selectedBlood || '',
  });

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === EMERGENCY_CONTRACEPTION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH,
  );

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: EMERGENCY_CONTRACEPTION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.MEDICAL_HISTORY}));
  };

  const renderBloodQuestion = () => {
    return (
      <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft">
        <UiText fontSize="lg" fontWeight="bold" mt={spacing.small}>
          What was your blood pressure reading?
        </UiText>
        <UiSimpleGrid columns={{base: 1, lg: 2}} spacing={5} mt={spacing.small}>
          {questionnaire?.bloodOptions?.map(value => (
            <UiButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedBlood === value.value ? 'solid' : 'toggle'}
              onClick={() => setInputs({selectedBlood: value.value})}
            />
          ))}
        </UiSimpleGrid>
      </UiBox>
    );
  };

  const renderDesc = () => {
    switch (inputs.selectedOption) {
      case 'YES':
        return renderBloodQuestion();
      case 'NO':
        return <Notice />;
      default:
        return null;
    }
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption || (inputs.selectedOption === 'YES' && !inputs.selectedBlood)}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small}>
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderDesc()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire9Screen;
